// ── 연차 관리 메인 훅 (상태·API·CRUD 핸들러) ──────────────────────────────────
import { useState, useEffect, useMemo } from 'react';
import { useToast } from '../../../components/Toast';
import { apiClient } from '../../../api/apiClient';
import { toDateString } from '../../../utils/date';
import { TYPE_TO_LOCKED_CATEGORY, FIXED_COUNT_MAP, TYPE_WITH_PAID_UNPAID_ONLY, PAID_UNPAID_CATEGORIES } from './constants';
import { emptyForm, createEmptyBulkRow, getIsMobile, addMonthsToDateText, isBeforeYmd, inferEffectDefaults } from './utils';

export function useLeaveManagement({ apiBase, syncLeavesEndpoint, settingsApiPath, settingsApiPathBuilder, selectedCompanyCodes }) {
  const { showToast } = useToast();

  // ── 반응형 ─────────────────────────────────────────────────────────────────
  const [isMobile, setIsMobile] = useState(getIsMobile());

  // ── 데이터 상태 ────────────────────────────────────────────────────────────
  const [nameQuery,              setNameQuery]              = useState('');
  const [users,                  setUsers]                  = useState([]);
  const [list,                   setList]                   = useState([]);
  const [summaryRows,            setSummaryRows]            = useState([]);
  const [eventTypes,             setEventTypes]             = useState([]);
  const [includeResignedSummary, setIncludeResignedSummary] = useState(false);
  const [sheetUrl,               setSheetUrl]               = useState('');

  const [activeLeaveFilters, setActiveLeaveFilters] = useState([]); // 요약 헤더 타입 필터 (다중)

  // ── 페이지네이션 ───────────────────────────────────────────────────────────
  const [currentPage, setCurrentPage] = useState(1);

  // ── 단건 추가/수정 모달 ────────────────────────────────────────────────────
  const [open,           setOpen]           = useState(false);
  const [editId,         setEditId]         = useState(null);
  const [form,           setForm]           = useState(emptyForm());
  const [userPickerText, setUserPickerText] = useState(''); // 수정 모드용 단일 직원 이름 입력

  // ── 신규 추가 전용: 복수 직원 × 복수 날짜 선택 상태 ──────────────────────
  const [selectedDates,       setSelectedDates]       = useState([]);
  const [selectedUserIds,     setSelectedUserIds]     = useState([]);
  const [showResignedInPicker, setShowResignedInPicker] = useState(false);

  // ── 반차기준시간 경고 판별용 출퇴근 시간 설정 ────────────────────────────
  const [attendanceSettings, setAttendanceSettings] = useState(null);
  const [attendanceSettingsByCompany, setAttendanceSettingsByCompany] = useState({});

  // ── 대량 붙여넣기 ──────────────────────────────────────────────────────────
  const [bulkOpen,       setBulkOpen]       = useState(false);
  const [bulkRows,       setBulkRows]       = useState(() => Array.from({ length: 12 }, createEmptyBulkRow));
  const [bulkSubmitting, setBulkSubmitting] = useState(false);

  // ── 직원명 → id 매핑 (핸들러 내부 사용) ──────────────────────────────────
  const userMap = useMemo(() => {
    const map = new Map();
    users.forEach((u) => map.set(String(u.name || '').trim(), u.id));
    return map;
  }, [users]);

  // 이름 → id 매핑 (단건 직원 검색용, 중복 이름 첫 번째만 사용)
  const userIdByExactName = useMemo(() => {
    const map = new Map();
    users.forEach((u) => {
      const key = String(u.name || '').trim();
      if (!key || map.has(key)) return;
      map.set(key, String(u.id));
    });
    return map;
  }, [users]);

  // 이펙트
  useEffect(() => { fetchAll(); }, [includeResignedSummary, JSON.stringify(selectedCompanyCodes || [])]); // eslint-disable-line
  useEffect(() => { setCurrentPage(1); }, [nameQuery]);
  useEffect(() => {
    const onResize = () => setIsMobile(getIsMobile());
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  async function fetchAttendanceSettings() {
    if (typeof settingsApiPathBuilder === 'function') {
      const rawCodes = Array.isArray(selectedCompanyCodes) && selectedCompanyCodes.length > 0
        ? selectedCompanyCodes
        : ['PINE'];
      const codes = [...new Set(rawCodes.map((code) => String(code || 'PINE').toUpperCase()))];
      const rows = await Promise.all(codes.map(async (code) => {
        const res = await apiClient.get(settingsApiPathBuilder(code));
        return { code, res };
      }));
      const byCompany = {};
      rows.forEach(({ code, res }) => {
        if (res.ok) byCompany[code] = res.data?.data || null;
      });
      return {
        ok: rows.every(({ res }) => res.ok),
        data: byCompany[codes[0]] || null,
        byCompany,
        message: rows.find(({ res }) => !res.ok)?.res?.data?.message,
      };
    }

    const res = await apiClient.get(settingsApiPath || '/api/admin/attendance-time-settings');
    const data = res.ok ? (res.data?.data || null) : null;
    return {
      ok: res.ok,
      data,
      byCompany: data ? { PINE: data } : {},
      message: res.data?.message,
    };
  }

  async function fetchAll() {
    const codesQ = Array.isArray(selectedCompanyCodes) && selectedCompanyCodes.length > 0 ? `&codes=${selectedCompanyCodes.join(',')}` : '';
    const [userRes, leaveRes, summaryRes, eventTypeRes, sheetRes, settingsRes] = await Promise.all([
      apiClient.get(`${apiBase}/users?include_resigned=true${codesQ}`),
      apiClient.get(`${apiBase}${codesQ ? `?${codesQ.slice(1)}` : ''}`),
      apiClient.get(`${apiBase}/summary?include_resigned=${includeResignedSummary}${codesQ}`),
      apiClient.get(`${apiBase}/event-types`),
      apiClient.get('/api/admin/sheets/info'),
      fetchAttendanceSettings(),
    ]);
    if (userRes.ok)     setUsers(userRes.data.list || []);
    if (leaveRes.ok)    setList(leaveRes.data || []);
    if (summaryRes.ok)  setSummaryRows(summaryRes.data || []);
    if (eventTypeRes.ok) setEventTypes(eventTypeRes.data?.list || []);
    if (sheetRes.ok)    setSheetUrl(sheetRes.data.url || '');
    if (settingsRes.ok) {
      setAttendanceSettings(settingsRes.data || null);
      setAttendanceSettingsByCompany(settingsRes.byCompany || {});
    }
    if (!leaveRes.ok)   showToast(leaveRes.data?.message   || '연차 목록 조회에 실패했습니다.', 'error');
    if (!summaryRes.ok) showToast(summaryRes.data?.message || '연차 요약 조회에 실패했습니다.', 'error');
    // 구글시트 정보는 보조 기능(시트 링크 표시) — 실패해도 화면 로드마다 토스트를 띄우지 않는다.
    //   미설정 환경(미팅링크: 구글 API "The caller does not have permission")과 고객 테넌트(의도된 403)에서
    //   회사 아코디언·연차 화면을 열 때마다 원문 오류가 노출되던 소음 제거. 링크만 조용히 숨긴다.
    //   시트 동기화 버튼의 실패는 각 액션 핸들러가 따로 토스트하므로 실제 장애는 여전히 보인다.
    if (!sheetRes.ok) setSheetUrl('');
  }

  // ── 폼 규칙 적용 (개수 자동 고정, 구분 잠금, 경조사 6개월 제한) ──────────
  function applyLeaveFormRules(nextForm, { notify = true, patch = {} } = {}) {
    const patched = { ...nextForm };
    const typeChanged = Object.prototype.hasOwnProperty.call(patch, 'type');

    // 고정 개수 종류이면 자동 고정, 그 외 종류 변경 시 1 기본값
    if (FIXED_COUNT_MAP[patched.type] !== undefined) {
      patched.count = FIXED_COUNT_MAP[patched.type];
    } else if (typeChanged) {
      patched.count = '1';
    }

    // 특정 종류는 구분이 고정됨
    const forceCategory = TYPE_TO_LOCKED_CATEGORY[patched.type];
    if (forceCategory) {
      patched.category = forceCategory;
    } else if (TYPE_WITH_PAID_UNPAID_ONLY.has(patched.type)) {
      if (!PAID_UNPAID_CATEGORIES.includes(String(patched.category || ''))) {
        patched.category = '유급';
      }
    }

    // 경조사: 입사 6개월 이전에는 선택 불가 (단건 폼에서만 실시간 검증)
    if (patched.type === '경조사') {
      const selectedUser = users.find((u) => String(u.id) === String(patched.user_id));
      const hireDate = toDateString(selectedUser?.hire_date || '');
      const leaveDate = toDateString(patched.date || '');
      const sixMonthDate = addMonthsToDateText(hireDate, 6);
      if (sixMonthDate && leaveDate && isBeforeYmd(leaveDate, sixMonthDate)) {
        if (notify) showToast('입사 6개월 이전에는 경조사를 선택할 수 없습니다. 연차로 자동 변경됩니다.', 'error');
        patched.type = '연차';
        patched.category = '유급';
      }
    }
    if (
      (typeChanged || Object.prototype.hasOwnProperty.call(patch, 'category'))
      && !Object.prototype.hasOwnProperty.call(patch, 'payroll_effect')
      && !Object.prototype.hasOwnProperty.call(patch, 'leave_balance_effect')
      && !Object.prototype.hasOwnProperty.call(patch, 'monthly_grant_effect')
      && !Object.prototype.hasOwnProperty.call(patch, 'calendar_effect')
    ) {
      Object.assign(patched, inferEffectDefaults(patched.type, patched.category));
      patched.event_type_id = '';
      patched.event_name = '';
    }
    return patched;
  }

  function updateFormWithRules(patch, options = { notify: true }) {
    setForm((prev) => applyLeaveFormRules({ ...prev, ...patch }, { ...options, patch }));
  }

  // ── 수정 모드: 단일 직원 검색 ─────────────────────────────────────────────
  function handleUserPickerChange(rawValue) {
    const text = String(rawValue || '');
    setUserPickerText(text);
    const userId = userIdByExactName.get(text.trim()) || '';
    updateFormWithRules({ user_id: userId }, { notify: false });
  }

  // ── 복수 직원 선택 헬퍼 ───────────────────────────────────────────────────
  function addUserToSelection(user) {
    if (selectedUserIds.includes(String(user.id))) {
      showToast('이미 선택된 직원입니다.', 'warning');
      return;
    }
    setSelectedUserIds((prev) => [...prev, String(user.id)]);
  }

  function removeUserFromSelection(index) {
    setSelectedUserIds((prev) => prev.filter((_, i) => i !== index));
  }

  function toggleUserSelection(user) {
    const uid = String(user.id);
    const idx = selectedUserIds.indexOf(uid);
    if (idx >= 0) removeUserFromSelection(idx);
    else addUserToSelection(user);
  }

  // ── 모달 열기/닫기 ────────────────────────────────────────────────────────
  function openCreate() {
    setEditId(null);
    setForm(emptyForm());
    setUserPickerText('');
    setSelectedDates([]);
    setSelectedUserIds([]);
    setOpen(true);
  }

  async function openEdit(row) {
    const leaveId = Number(row?.id);
    let latest = row;
    if (Number.isInteger(leaveId) && leaveId > 0) {
      const result = await apiClient.get(`${apiBase}/${leaveId}`);
      if (!result.ok) {
        showToast(result.data?.message || '최신 연차 항목을 불러오지 못했습니다.', 'error');
        return;
      }
      latest = result.data?.leave || row;
    }
    setEditId(latest.id);
    setForm({
      user_id: String(latest.user_id),
      date: toDateString(latest.date),
      type: latest.type,
      category: latest.category,
      count: String(latest.count),
      reason: latest.reason || '',
      event_type_id: latest.event_type_id ? String(latest.event_type_id) : '',
      event_name: latest.event_name || '',
      payroll_effect: latest.payroll_effect || inferEffectDefaults(latest.type, latest.category).payroll_effect,
      leave_balance_effect: latest.leave_balance_effect || inferEffectDefaults(latest.type, latest.category).leave_balance_effect,
      monthly_grant_effect: latest.monthly_grant_effect || inferEffectDefaults(latest.type, latest.category).monthly_grant_effect,
      calendar_effect: latest.calendar_effect || inferEffectDefaults(latest.type, latest.category).calendar_effect,
      grant_decision_source: latest.grant_decision_source || 'manual',
      affects_weekly_holiday: latest.affects_weekly_holiday !== false, // 기본 true, false 명시만 주휴 면제
    });
    setUserPickerText(latest.name || '');
    setOpen(true);
  }

  // ── 저장 ──────────────────────────────────────────────────────────────────
  async function save() {
    // 수정 모드: 기존 단건 처리
    if (editId) {
      if (!form.user_id || !form.date || !form.type || !form.category || !form.count) {
        showToast('필수 항목을 입력해주세요.', 'error');
        return;
      }
      if (String(userPickerText || '').trim() && !form.user_id) {
        showToast('직원 이름을 목록에서 정확히 선택해 주세요.', 'error');
        return;
      }
      const payload = {
        user_id: Number(form.user_id),
        date: form.date, type: form.type, category: form.category,
        count: Number(form.count), reason: form.reason,
        event_type_id: form.event_type_id ? Number(form.event_type_id) : null,
        event_name: form.event_name || null,
        payroll_effect: form.payroll_effect,
        leave_balance_effect: form.leave_balance_effect,
        monthly_grant_effect: form.monthly_grant_effect,
        calendar_effect: form.calendar_effect,
        grant_decision_source: form.grant_decision_source || 'manual',
        affects_weekly_holiday: form.affects_weekly_holiday !== false,
      };
      const result = await apiClient.put(`${apiBase}/${editId}`, payload);
      if (!result.ok) { showToast(result.data?.message || '저장에 실패했습니다.', 'error'); return; }
      showToast('연차를 수정했습니다.', 'success');
      setOpen(false);
      fetchAll();
      return;
    }

    // 신규 추가 모드: 복수 직원 × 복수 날짜
    if (selectedUserIds.length === 0) { showToast('직원을 한 명 이상 선택해 주세요.', 'error'); return; }
    if (selectedDates.length === 0)   { showToast('날짜를 한 개 이상 선택해 주세요.', 'error'); return; }
    if (!form.type || !form.category || !form.count) {
      showToast('종류, 구분, 개수를 입력해주세요.', 'error');
      return;
    }

    // 경조사: 모든 직원-날짜 조합에 대해 입사 6개월 규칙 사전 검증
    if (form.type === '경조사') {
      for (const userId of selectedUserIds) {
        const u = users.find((u) => String(u.id) === userId);
        const sixMonthDate = addMonthsToDateText(toDateString(u?.hire_date || ''), 6);
        if (sixMonthDate) {
          const violated = selectedDates.filter((d) => isBeforeYmd(d, sixMonthDate));
          if (violated.length > 0) {
            showToast(`${u?.name}: 입사 6개월 이전 날짜(${violated.join(', ')})에는 경조사를 선택할 수 없습니다.`, 'error');
            return;
          }
        }
      }
    }

    // M명 × N일 = M×N건 레코드 생성
    const records = [];
    for (const userId of selectedUserIds) {
      for (const date of selectedDates) {
        records.push({
          user_id: Number(userId),
          date, type: form.type, category: form.category,
          count: Number(form.count), reason: form.reason || null,
          event_type_id: form.event_type_id ? Number(form.event_type_id) : null,
          event_name: form.event_name || null,
          payroll_effect: form.payroll_effect,
          leave_balance_effect: form.leave_balance_effect,
          monthly_grant_effect: form.monthly_grant_effect,
          calendar_effect: form.calendar_effect,
          grant_decision_source: form.grant_decision_source || 'manual',
          affects_weekly_holiday: form.affects_weekly_holiday !== false,
        });
      }
    }

    // 1건이면 단건 API, 2건 이상이면 bulk API 활용
    const result = records.length === 1
      ? await apiClient.post(apiBase, records[0])
      : await apiClient.post(`${apiBase}/bulk`, { rows: records });

    if (!result.ok) { showToast(result.data?.message || '저장에 실패했습니다.', 'error'); return; }
    showToast(`${records.length}건을 등록했습니다.`, 'success');
    setOpen(false);
    fetchAll();
  }

  // ── 삭제 ──────────────────────────────────────────────────────────────────
  async function remove(row) {
    if (!window.confirm('이 연차 항목을 삭제할까요?')) return;
    const result = await apiClient.del(`${apiBase}/${row.id}`);
    if (!result.ok) { showToast(result.data?.message || '삭제에 실패했습니다.', 'error'); return; }
    showToast('연차를 삭제했습니다.', 'success');
    fetchAll();
  }

  // ── 구글시트 동기화 ───────────────────────────────────────────────────────
  async function syncGoogleSheet() {
    const result = await apiClient.post(syncLeavesEndpoint, {});
    if (!result.ok) { showToast(result.data?.message || '구글시트 동기화에 실패했습니다.', 'error'); return; }
    setSheetUrl(result.data.url || sheetUrl);
    showToast(`구글시트 동기화 완료 (${result.data.count || 0}건)`, 'success');
  }

  return {
    // 상태
    isMobile, nameQuery, setNameQuery,
    activeLeaveFilters, setActiveLeaveFilters,
    users, list, summaryRows, eventTypes,
    includeResignedSummary, setIncludeResignedSummary,
    sheetUrl, currentPage, setCurrentPage,
    open, setOpen, editId,
    form, setForm, userPickerText,
    selectedDates, setSelectedDates,
    selectedUserIds, setSelectedUserIds,
    showResignedInPicker, setShowResignedInPicker,
    attendanceSettings, attendanceSettingsByCompany,
    bulkOpen, setBulkOpen,
    bulkRows, setBulkRows,
    bulkSubmitting, setBulkSubmitting,
    // 파생값 (핸들러용)
    userMap, userIdByExactName,
    // 핸들러
    fetchAll, updateFormWithRules,
    handleUserPickerChange,
    addUserToSelection, removeUserFromSelection, toggleUserSelection,
    openCreate, openEdit, save, remove, syncGoogleSheet,
  };
}
